/**
 * Storage / torrent-domain events — catalog 1.19.0 additions (commerce-platform
 * phase-8b "P8b Player & Torrents", plan P5-T06 "catalog v13").
 *
 * NOTE (catalog release naming, mirrors the drift notes in catalog/media.ts and
 * catalog/subscriptions.ts): the plan's "v13" is plan fiction. The latest real
 * catalog release before this file is 1.18.0 (the `media:*` v12 completion), so
 * these definitions land in the next real release, `since: '1.19.0'`.
 *
 * Transport: like every existing `storage:*` realtime notification these ride
 * the raw Redis channel `storage:events` (services/storage-worker
 * StorageEventsPublisher, worker-local precedent), consumed by the gateway's
 * dedicated `WsStorageEventsBridge`, which re-emits to the `storage:updates`
 * room. The bridge passes any event type through — no per-type registration is
 * needed there. These types are deliberately NOT in FANOUT_EXCLUDED_EVENT_TYPES
 * (low-frequency lifecycle events, every one auditable) and `storage` is
 * intentionally absent from ROOM_BY_SOURCE (raw-channel bridge owns delivery;
 * adding it to the durable fan-out would double-emit — see the exclusion
 * comment in ../room-registry.ts).
 *
 * ORGANIZATION SCOPE: the storage domain is ownership-modelled (`ownerUserId`,
 * `StorageEvent.ownerId`), never org-scoped — verified against the worker and
 * service schemas. `orgId` is therefore OPTIONAL in every payload below: an
 * emitter that knows the tenant may include it (forward-compatible for a future
 * org-scoped storage room), but the lifecycle machinery has no tenant claim to
 * attach today. No payload ever carries bytes, paths, credentials or tracker
 * URLs — ids and reason strings only.
 *
 * AUDIT JUDGMENT (all five auditable:true; two flagged "per judgment" by the
 * plan and documented here):
 * - `mounted` / `promoted` — auditable per plan (capacity + cost transitions).
 * - `unmounted` — auditable TRUE by judgment: an unmount changes an asset's
 *   serving path and is the paired terminal of the auditable `mounted` row;
 *   auditing only half of a state-machine edge would leave the tail incomplete.
 * - `revival-seeded` — auditable TRUE by judgment: seeding spends platform
 *   egress on behalf of a cold asset; it is an operator-relevant action and
 *   P8b-A2 forbids silent revival handling.
 * - `unavailable` — auditable TRUE: silent failure is forbidden (P8b-A2); the
 *   audit row is the never-silent guarantee made queryable.
 *
 * - `storage:torrent-mounted`      — a cold-mount window opened (registry key
 *   set with the idle TTL EX; online for `ttlSeconds`).
 * - `storage:torrent-unmounted`    — the window closed (reason: `idle_ttl` |
 *   `manual` | `max_downloads`, mirroring the runner vocabulary). NOTE: idle-TTL
 *   expiry enforced by Redis key expiry fires no process — the join key is the
 *   mount row's `occurredAt + ttlSeconds`.
 * - `storage:torrent-promoted`     — a torrent was promoted to a static copy
 *   (DEFINITION ONLY today: promotion automation is operator-driven and not
 *   implemented — docs/storage/TORRENT-PROVIDER.md §Promotion; the emit site
 *   lands with the promotion automation task, see the popularity counter in
 *   services/storage-worker/src/torrent/torrent.runner.ts).
 * - `storage:torrent-revival-seeded` — a dead torrent entered a temporary
 *   revival-seeding window (`revival_seed_seconds`).
 * - `storage:torrent-unavailable`  — a cold serve could not proceed (dead
 *   torrent after revival, no revival window, or an expired session) — loud,
 *   never silent (P8b-A2).
 */
import {
  asEventRecord,
  defineEvent,
  optionalNumber,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

/** Local optional-boolean gate (registry has no boolean primitive; style mirrors the local helpers in subscriptions.ts). */
function optionalBoolean(value: unknown, field: string, context: string): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'boolean') {
    throw new Error(`${context}: field "${field}" must be a boolean`);
  }
  return value;
}

// ---------------------------------------------------------------------------
// Payload types (ids + reason strings + counters only — never bytes/paths)
// ---------------------------------------------------------------------------

export interface StorageTorrentMountedPayload extends Record<string, unknown> {
  orgId?: string;
  fileId: string;
  manifestKey?: string;
  peers?: number;
  ttlSeconds?: number;
  revivalSeeded?: boolean;
  occurredAt: string;
}

export interface StorageTorrentUnmountedPayload extends Record<string, unknown> {
  orgId?: string;
  fileId: string;
  manifestKey?: string;
  /** Runner vocabulary: 'idle_ttl' | 'manual' | 'max_downloads'. */
  reason?: string;
  occurredAt: string;
}

export interface StorageTorrentPromotedPayload extends Record<string, unknown> {
  orgId?: string;
  fileId: string;
  manifestKey?: string;
  /** Storage file id of the static copy created by the promotion. */
  staticFileId?: string;
  /** Popularity counter value that crossed the promotion threshold. */
  popularity?: number;
  occurredAt: string;
}

export interface StorageTorrentRevivalSeededPayload extends Record<string, unknown> {
  orgId?: string;
  fileId: string;
  manifestKey?: string;
  /** Seeding window granted (revival_seed_seconds). */
  seedSeconds?: number;
  /** Peers observed at seed completion. */
  peers?: number;
  occurredAt: string;
}

export interface StorageTorrentUnavailablePayload extends Record<string, unknown> {
  orgId?: string;
  fileId: string;
  manifestKey?: string;
  /** Required — the never-silent guarantee (P8b-A2): every unavailability names its cause. */
  reason: string;
  occurredAt: string;
}

// ---------------------------------------------------------------------------
// Definitions (media.ts style: TYPE const -> schema -> defineEvent -> record)
// ---------------------------------------------------------------------------

const TORRENT_MOUNTED_TYPE = 'storage:torrent-mounted';
const torrentMountedSchema: EventSchema<StorageTorrentMountedPayload> = {
  parse(data) {
    const record = asEventRecord(data, TORRENT_MOUNTED_TYPE);
    return {
      orgId: optionalString(record.orgId, 'orgId', TORRENT_MOUNTED_TYPE),
      fileId: requireString(record.fileId, 'fileId', TORRENT_MOUNTED_TYPE),
      manifestKey: optionalString(record.manifestKey, 'manifestKey', TORRENT_MOUNTED_TYPE),
      peers: optionalNumber(record.peers, 'peers', TORRENT_MOUNTED_TYPE),
      ttlSeconds: optionalNumber(record.ttlSeconds, 'ttlSeconds', TORRENT_MOUNTED_TYPE),
      revivalSeeded: optionalBoolean(record.revivalSeeded, 'revivalSeeded', TORRENT_MOUNTED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', TORRENT_MOUNTED_TYPE),
    };
  },
};

export const STORAGE_TORRENT_MOUNTED_EVENT = defineEvent<StorageTorrentMountedPayload>({
  type: TORRENT_MOUNTED_TYPE,
  version: 1,
  schema: torrentMountedSchema,
  auditable: true,
  description:
    'A cold torrent mount window opened (registry online state set with the idle TTL; ids, counters and window seconds only).',
  since: '1.19.0',
});

const TORRENT_UNMOUNTED_TYPE = 'storage:torrent-unmounted';
const torrentUnmountedSchema: EventSchema<StorageTorrentUnmountedPayload> = {
  parse(data) {
    const record = asEventRecord(data, TORRENT_UNMOUNTED_TYPE);
    return {
      orgId: optionalString(record.orgId, 'orgId', TORRENT_UNMOUNTED_TYPE),
      fileId: requireString(record.fileId, 'fileId', TORRENT_UNMOUNTED_TYPE),
      manifestKey: optionalString(record.manifestKey, 'manifestKey', TORRENT_UNMOUNTED_TYPE),
      reason: optionalString(record.reason, 'reason', TORRENT_UNMOUNTED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', TORRENT_UNMOUNTED_TYPE),
    };
  },
};

export const STORAGE_TORRENT_UNMOUNTED_EVENT = defineEvent<StorageTorrentUnmountedPayload>({
  type: TORRENT_UNMOUNTED_TYPE,
  version: 1,
  schema: torrentUnmountedSchema,
  auditable: true,
  description:
    'A cold torrent mount window closed (reason idle_ttl/manual/max_downloads; the auditable terminal of the mounted state-machine edge — Redis EXpiry of a mount key fires no process and is joined via mounted.occurredAt + ttlSeconds).',
  since: '1.19.0',
});

const TORRENT_PROMOTED_TYPE = 'storage:torrent-promoted';
const torrentPromotedSchema: EventSchema<StorageTorrentPromotedPayload> = {
  parse(data) {
    const record = asEventRecord(data, TORRENT_PROMOTED_TYPE);
    return {
      orgId: optionalString(record.orgId, 'orgId', TORRENT_PROMOTED_TYPE),
      fileId: requireString(record.fileId, 'fileId', TORRENT_PROMOTED_TYPE),
      manifestKey: optionalString(record.manifestKey, 'manifestKey', TORRENT_PROMOTED_TYPE),
      staticFileId: optionalString(record.staticFileId, 'staticFileId', TORRENT_PROMOTED_TYPE),
      popularity: optionalNumber(record.popularity, 'popularity', TORRENT_PROMOTED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', TORRENT_PROMOTED_TYPE),
    };
  },
};

export const STORAGE_TORRENT_PROMOTED_EVENT = defineEvent<StorageTorrentPromotedPayload>({
  type: TORRENT_PROMOTED_TYPE,
  version: 1,
  schema: torrentPromotedSchema,
  auditable: true,
  description:
    'A popular torrent was promoted to a static copy (threshold crossed; ids and popularity counter only). DEFINITION ONLY until promotion automation lands — see docs/storage/TORRENT-PROVIDER.md.',
  since: '1.19.0',
});

const TORRENT_REVIVAL_SEEDED_TYPE = 'storage:torrent-revival-seeded';
const torrentRevivalSeededSchema: EventSchema<StorageTorrentRevivalSeededPayload> = {
  parse(data) {
    const record = asEventRecord(data, TORRENT_REVIVAL_SEEDED_TYPE);
    return {
      orgId: optionalString(record.orgId, 'orgId', TORRENT_REVIVAL_SEEDED_TYPE),
      fileId: requireString(record.fileId, 'fileId', TORRENT_REVIVAL_SEEDED_TYPE),
      manifestKey: optionalString(record.manifestKey, 'manifestKey', TORRENT_REVIVAL_SEEDED_TYPE),
      seedSeconds: optionalNumber(record.seedSeconds, 'seedSeconds', TORRENT_REVIVAL_SEEDED_TYPE),
      peers: optionalNumber(record.peers, 'peers', TORRENT_REVIVAL_SEEDED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', TORRENT_REVIVAL_SEEDED_TYPE),
    };
  },
};

export const STORAGE_TORRENT_REVIVAL_SEEDED_EVENT = defineEvent<StorageTorrentRevivalSeededPayload>(
  {
    type: TORRENT_REVIVAL_SEEDED_TYPE,
    version: 1,
    schema: torrentRevivalSeededSchema,
    auditable: true,
    description:
      'A zero-peer torrent entered a temporary platform revival-seeding window (operator-relevant egress spend; seconds and peer counts only).',
    since: '1.19.0',
  },
);

const TORRENT_UNAVAILABLE_TYPE = 'storage:torrent-unavailable';
const torrentUnavailableSchema: EventSchema<StorageTorrentUnavailablePayload> = {
  parse(data) {
    const record = asEventRecord(data, TORRENT_UNAVAILABLE_TYPE);
    return {
      orgId: optionalString(record.orgId, 'orgId', TORRENT_UNAVAILABLE_TYPE),
      fileId: requireString(record.fileId, 'fileId', TORRENT_UNAVAILABLE_TYPE),
      manifestKey: optionalString(record.manifestKey, 'manifestKey', TORRENT_UNAVAILABLE_TYPE),
      reason: requireString(record.reason, 'reason', TORRENT_UNAVAILABLE_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', TORRENT_UNAVAILABLE_TYPE),
    };
  },
};

export const STORAGE_TORRENT_UNAVAILABLE_EVENT = defineEvent<StorageTorrentUnavailablePayload>({
  type: TORRENT_UNAVAILABLE_TYPE,
  version: 1,
  schema: torrentUnavailableSchema,
  auditable: true,
  description:
    'A cold torrent serve could not proceed (dead torrent after revival, no revival window, or expired session). Reason is REQUIRED — silent failure is forbidden (P8b-A2).',
  since: '1.19.0',
});

export const STORAGE_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [STORAGE_TORRENT_MOUNTED_EVENT.type]: STORAGE_TORRENT_MOUNTED_EVENT,
  [STORAGE_TORRENT_UNMOUNTED_EVENT.type]: STORAGE_TORRENT_UNMOUNTED_EVENT,
  [STORAGE_TORRENT_PROMOTED_EVENT.type]: STORAGE_TORRENT_PROMOTED_EVENT,
  [STORAGE_TORRENT_REVIVAL_SEEDED_EVENT.type]: STORAGE_TORRENT_REVIVAL_SEEDED_EVENT,
  [STORAGE_TORRENT_UNAVAILABLE_EVENT.type]: STORAGE_TORRENT_UNAVAILABLE_EVENT,
};
